import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconHistory,
  IconPencil,
  IconPlugConnected,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useTranslation } from 'react-i18next';
import { getAdapterMeta } from './adapters/registry';
import type { Integration } from './types';

export interface IntegrationsListProps {
  integrations: Integration[];
  loading?: boolean;
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onCreate: () => void;
  onEdit: (integration: Integration) => void;
  onDelete: (integration: Integration) => void;
  onViewDeliveries: (integration: Integration) => void;
}

function formatTimestamp(ts: string): string {
  return new Date(ts).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Paginated table of a user's integrations.
 *
 * The component is purely presentational — pagination state, fetch lifecycle,
 * and CRUD calls all live in the parent. Pass in the current page slice via
 * `integrations` and the matching `totalCount` for pager rendering.
 */
export function IntegrationsList({
  integrations,
  loading,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onCreate,
  onEdit,
  onDelete,
  onViewDeliveries,
}: IntegrationsListProps) {
  const { t } = useTranslation();

  const columns = [
    {
      accessor: 'title',
      title: t('integrations.list.title'),
      render: (row: Integration) => (
        <Text size="sm" fw={500}>
          {row.title}
        </Text>
      ),
    },
    {
      accessor: 'kind',
      title: t('integrations.list.kind'),
      render: (row: Integration) => {
        const meta = getAdapterMeta(row.kind);
        return <Text size="sm">{meta ? t(meta.label) : row.kind}</Text>;
      },
    },
    {
      accessor: 'capabilities',
      title: t('integrations.list.capabilities'),
      render: (row: Integration) => {
        const meta = getAdapterMeta(row.kind);
        if (!meta) return null;
        const caps = meta.capabilities;
        return (
          <Group gap={4} wrap="wrap">
            {caps.hmac && (
              <Badge variant="light" color="blue" size="sm">
                {t('integrations.capabilities.hmac')}
              </Badge>
            )}
            {caps.retry && (
              <Badge variant="light" color="grape" size="sm">
                {t('integrations.capabilities.retry')}
              </Badge>
            )}
            {caps.replayable && (
              <Badge variant="light" color="teal" size="sm">
                {t('integrations.capabilities.replayable')}
              </Badge>
            )}
            {caps.idempotency && (
              <Badge variant="light" color="cyan" size="sm">
                {t('integrations.capabilities.idempotency')}
              </Badge>
            )}
          </Group>
        );
      },
    },
    {
      accessor: 'created_at',
      title: t('integrations.list.created'),
      render: (row: Integration) => (
        <Text size="sm" c="dimmed">
          {formatTimestamp(row.created_at)}
        </Text>
      ),
    },
    {
      accessor: 'actions',
      title: t('integrations.list.actions'),
      textAlign: 'right' as const,
      render: (row: Integration) => (
        <Group gap={4} justify="flex-end" wrap="nowrap">
          <Tooltip label={t('integrations.actions.viewDeliveries')}>
            <ActionIcon
              variant="subtle"
              onClick={(e) => {
                e.stopPropagation();
                onViewDeliveries(row);
              }}
            >
              <IconHistory size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('integrations.actions.edit')}>
            <ActionIcon
              variant="subtle"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row);
              }}
            >
              <IconPencil size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('integrations.actions.delete')}>
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(row);
              }}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      ),
    },
  ];

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button leftSection={<IconPlus size={16} />} onClick={onCreate}>
          {t('integrations.actions.create')}
        </Button>
      </Group>
      <DataTable
        minHeight={integrations.length === 0 ? 280 : undefined}
        withTableBorder
        borderRadius="md"
        shadow="xs"
        records={integrations}
        columns={columns}
        totalRecords={totalCount}
        recordsPerPage={pageSize}
        page={page}
        onPageChange={onPageChange}
        fetching={loading}
        noRecordsText=""
        emptyState={
          integrations.length === 0 ? (
            <Box py="xl" px="md">
              <Stack align="center" gap="xs">
                <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                  <IconPlugConnected size={24} />
                </ThemeIcon>
                <Text size="sm" fw={500}>
                  {t('integrations.empty.title')}
                </Text>
                <Text size="sm" c="dimmed" ta="center" maw={360}>
                  {t('integrations.empty.description')}
                </Text>
              </Stack>
            </Box>
          ) : (
            <></>
          )
        }
      />
    </Stack>
  );
}
